import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = '/api';

export default function ExpiryCommandCenter({ user, onNavigate }) {
    const [activeTab, setActiveTab] = useState('All');
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);
    const isSuperadmin = user?.userType === 'admin';

    const fetchInitialData = async () => {
        if (!operatorId && !isSuperadmin) return;
        setLoading(true);
        try {
            const drvUrl = isSuperadmin ? `/api/drivers/all` : `/api/drivers/${operatorId}`;
            const vehUrl = isSuperadmin ? `/api/vehicles/${operatorId === null ? 'all' : operatorId}` : `/api/vehicles/${operatorId}`;
            const cliUrl = isSuperadmin ? `/api/clients/all` : `/api/clients/${operatorId}`;
            
            const [drvRes, vehRes, cliRes] = await Promise.all([
                axios.get(drvUrl).catch(() => ({ data: [] })),
                axios.get(vehUrl).catch(() => ({ data: [] })),
                axios.get(cliUrl).catch(() => ({ data: [] }))
            ]);
            
            setDrivers(Array.isArray(drvRes.data) ? drvRes.data : []);
            setVehicles(Array.isArray(vehRes.data) ? vehRes.data : []);
            setClients(Array.isArray(cliRes.data) ? cliRes.data : []);
        } catch (err) {
            console.error('Error fetching expiry data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [operatorId, isSuperadmin]);

    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Compute expired drivers
    const expiredDriversList = useMemo(() => {
        return drivers.filter(driver => {
            let isExp = false;
            if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < todayStr) isExp = true;
            if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < todayStr) isExp = true;
            if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < todayStr) isExp = true;
            return isExp;
        });
    }, [drivers, todayStr]);

    // Compute expired vehicles
    const expiredVehiclesList = useMemo(() => {
        return vehicles.filter(vehicle => {
            let isExp = false;
            if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < todayStr) isExp = true;
            if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < todayStr) isExp = true;
            if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < todayStr) isExp = true;
            return isExp;
        });
    }, [vehicles, todayStr]);

    // Compute expired client records (contracts and documents)
    const expiredClientsList = useMemo(() => {
        const results = [];
        clients.forEach(client => {
            const expiredItems = [];
            
            // Check contracts
            if (Array.isArray(client.contracts)) {
                client.contracts.forEach(c => {
                    if (c.endDate && String(c.endDate).split('T')[0] < todayStr) {
                        expiredItems.push({
                            type: 'Contract',
                            title: c.title || 'Corporate Agreement',
                            expiryDate: c.endDate,
                            value: c.value,
                            fileName: c.fileName
                        });
                    }
                });
            }

            // Check documents
            if (Array.isArray(client.documents)) {
                client.documents.forEach(doc => {
                    if (doc.expiryDate && String(doc.expiryDate).split('T')[0] < todayStr) {
                        expiredItems.push({
                            type: 'Compliance Doc',
                            title: doc.title || doc.type || 'Legal Document',
                            expiryDate: doc.expiryDate,
                            fileName: doc.fileName
                        });
                    }
                });
            }

            if (expiredItems.length > 0) {
                results.push({
                    client,
                    expiredItems
                });
            }
        });
        return results;
    }, [clients, todayStr]);

    const totalExpiredCount = expiredDriversList.length + expiredVehiclesList.length + expiredClientsList.reduce((acc, c) => acc + c.expiredItems.length, 0);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    };

    const isExpired = (dateStr) => {
        if (!dateStr) return false;
        return String(dateStr).split('T')[0] < todayStr;
    };

    const getDaysExpired = (dateStr) => {
        if (!dateStr) return 0;
        const exp = new Date(dateStr);
        const now = new Date();
        const diffTime = now.getTime() - exp.getTime();
        return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    };

    const renderDateCell = (dateStr) => {
        if (!dateStr) return <span style={{ color: '#94a3b8' }}>—</span>;
        const exp = isExpired(dateStr);
        const days = getDaysExpired(dateStr);

        if (exp) {
            return (
                <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 14.5 }}>{formatDate(dateStr)}</span>
                    <span style={{ 
                        fontSize: 11.5, 
                        fontWeight: 700, 
                        color: '#b91c1c', 
                        background: '#fef2f2', 
                        padding: '3px 8px', 
                        borderRadius: 6, 
                        border: '1px solid #fee2e2',
                        width: 'fit-content'
                    }}>
                        Expired {days > 0 ? `${days}d ago` : 'today'}
                    </span>
                </div>
            );
        }
        return <span style={{ color: '#475569', fontWeight: 600, fontSize: 14 }}>{formatDate(dateStr)}</span>;
    };

    const handleNavigateTo = (page, id, tab) => {
        if (onNavigate) {
            onNavigate(page, id, tab);
        } else {
            window.location.hash = id ? (tab ? `${page}/${id}/${tab}` : `${page}/${id}`) : page;
        }
    };

    // Unified list of all expired records for 'All' view
    const allExpiredList = useMemo(() => {
        const list = [];
        // Drivers
        expiredDriversList.forEach(driver => {
            const expList = [];
            let earliestDate = null;
            if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < todayStr) {
                expList.push(`Civil ID`);
                earliestDate = driver.civilIdExpiry;
            }
            if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < todayStr) {
                expList.push(`Passport`);
                if (!earliestDate || driver.passportExpiry < earliestDate) earliestDate = driver.passportExpiry;
            }
            if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < todayStr) {
                expList.push(`License`);
                if (!earliestDate || driver.licenseExpiry < earliestDate) earliestDate = driver.licenseExpiry;
            }
            list.push({
                id: `drv-${driver._id}`,
                entityType: 'Driver',
                categoryColor: '#2563eb',
                categoryBg: '#eff6ff',
                categoryBorder: '#bfdbfe',
                name: driver.name || 'Driver',
                code: driver.driverCode || driver.driverId || 'ID Pending',
                contact: driver.phone || driver.email || '—',
                expiredDetail: expList.join(', '),
                expiryDate: earliestDate,
                avatarInitial: (driver.name || 'D').charAt(0),
                avatarBg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                action: () => handleNavigateTo('drivers', driver._id),
                actionText: 'View Profile'
            });
        });

        // Vehicles
        expiredVehiclesList.forEach(vehicle => {
            const expList = [];
            let earliestDate = null;
            if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < todayStr) {
                expList.push(`Insurance`);
                earliestDate = vehicle.insuranceExpiry;
            }
            if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < todayStr) {
                expList.push(`Registration`);
                if (!earliestDate || vehicle.registrationExpiry < earliestDate) earliestDate = vehicle.registrationExpiry;
            }
            if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < todayStr) {
                expList.push(`Permit`);
                if (!earliestDate || vehicle.vehicleLicenseExpiry < earliestDate) earliestDate = vehicle.vehicleLicenseExpiry;
            }
            list.push({
                id: `veh-${vehicle._id}`,
                entityType: 'Vehicle',
                categoryColor: '#7c3aed',
                categoryBg: '#f5f3ff',
                categoryBorder: '#ddd6fe',
                name: `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || vehicle.plateNumber,
                code: vehicle.plateNumber || vehicle.vehicleCode || 'No Plate',
                contact: vehicle.year ? `Year ${vehicle.year}` : 'Fleet Asset',
                expiredDetail: expList.join(', '),
                expiryDate: earliestDate,
                avatarInitial: 'V',
                avatarBg: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                action: () => handleNavigateTo('all-vehicles', vehicle._id),
                actionText: 'View Asset'
            });
        });

        // Clients
        expiredClientsList.forEach(cData => {
            cData.expiredItems.forEach((item, idx) => {
                list.push({
                    id: `cli-${cData.client._id}-${idx}`,
                    entityType: 'Client',
                    categoryColor: '#d97706',
                    categoryBg: '#fffbeb',
                    categoryBorder: '#fde68a',
                    name: cData.client.companyName || 'Corporate Client',
                    code: item.title,
                    contact: cData.client.contactPerson || cData.client.email || '—',
                    expiredDetail: `${item.type}: ${item.title}`,
                    expiryDate: item.expiryDate,
                    avatarInitial: (cData.client.companyName || 'C').charAt(0),
                    avatarBg: 'linear-gradient(135deg, #063A5C 0%, #1e40af 100%)',
                    action: () => handleNavigateTo('client-details', cData.client._id, 'contracts'),
                    actionText: 'View Client'
                });
            });
        });

        return list;
    }, [expiredDriversList, expiredVehiclesList, expiredClientsList, todayStr]);

    // Filtered lists based on search
    const filteredAll = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return allExpiredList.filter(item => 
            !q || 
            (item.name || '').toLowerCase().includes(q) ||
            (item.code || '').toLowerCase().includes(q) ||
            (item.contact || '').toLowerCase().includes(q) ||
            (item.expiredDetail || '').toLowerCase().includes(q) ||
            (item.entityType || '').toLowerCase().includes(q)
        );
    }, [allExpiredList, searchQuery]);

    const filteredDrivers = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return expiredDriversList.filter(d => 
            !q || 
            (d.name || '').toLowerCase().includes(q) ||
            (d.driverCode || '').toLowerCase().includes(q) ||
            (d.phone || '').toLowerCase().includes(q) ||
            (d.email || '').toLowerCase().includes(q)
        );
    }, [expiredDriversList, searchQuery]);

    const filteredVehicles = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return expiredVehiclesList.filter(v => 
            !q || 
            (v.plateNumber || '').toLowerCase().includes(q) ||
            (v.vehicleCode || '').toLowerCase().includes(q) ||
            (v.make || '').toLowerCase().includes(q) ||
            (v.model || '').toLowerCase().includes(q)
        );
    }, [expiredVehiclesList, searchQuery]);

    const filteredClients = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return expiredClientsList.filter(c => 
            !q || 
            (c.client?.companyName || '').toLowerCase().includes(q) ||
            (c.client?.contactPerson || '').toLowerCase().includes(q) ||
            (c.client?.email || '').toLowerCase().includes(q) ||
            c.expiredItems.some(item => (item.title || '').toLowerCase().includes(q))
        );
    }, [expiredClientsList, searchQuery]);

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Outfit', 'Plus Jakarta Sans', sans-serif" }}>
            <style>{`
                .ecc-container {
                    padding: 36px 44px;
                    max-width: 1440px;
                    margin: 0 auto;
                }
                .ecc-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .ecc-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    font-size: 12.5px;
                    font-weight: 800;
                    color: #dc2626;
                    background: #fef2f2;
                    border: 1px solid #fee2e2;
                    padding: 5px 12px;
                    border-radius: 99px;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    margin-bottom: 12px;
                }
                .ecc-title {
                    font-size: 32px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: -0.02em;
                }
                .ecc-desc {
                    color: #64748b;
                    font-size: 15.5px;
                    margin: 8px 0 0;
                    font-weight: 500;
                }
                .ecc-kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                    margin-bottom: 30px;
                }
                @media (max-width: 1024px) {
                    .ecc-kpi-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (max-width: 640px) {
                    .ecc-kpi-grid { grid-template-columns: 1fr; }
                }
                .ecc-kpi-card {
                    background: white;
                    border-radius: 18px;
                    border: 1.5px solid #e2e8f0;
                    padding: 22px 26px;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.02);
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                    user-select: none;
                }
                .ecc-kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
                    border-color: #cbd5e1;
                }
                .ecc-kpi-card.active {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15), 0 10px 24px rgba(37, 99, 235, 0.08);
                    background: #ffffff;
                }
                .ecc-kpi-icon {
                    width: 54px;
                    height: 54px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .ecc-kpi-val {
                    font-size: 32px;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1.1;
                }
                .ecc-kpi-label {
                    font-size: 14.5px;
                    font-weight: 600;
                    color: #64748b;
                    margin-top: 4px;
                }
                .ecc-controls {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 22px;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .ecc-tabs-group {
                    display: flex;
                    background: #f1f5f9;
                    padding: 5px;
                    border-radius: 14px;
                    gap: 6px;
                }
                .ecc-tab-btn {
                    padding: 10px 20px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 14.5px;
                    font-weight: 700;
                    font-family: inherit;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .ecc-tab-btn.active {
                    background: white;
                    color: #0f172a;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
                }
                .ecc-tab-badge {
                    padding: 3px 8px;
                    border-radius: 99px;
                    font-size: 12px;
                    font-weight: 800;
                    line-height: 1;
                }
                .ecc-search-input {
                    padding: 11px 18px 11px 40px;
                    border-radius: 12px;
                    border: 1.5px solid #cbd5e1;
                    font-size: 14.5px;
                    font-family: inherit;
                    outline: none;
                    width: 280px;
                    transition: all 0.2s;
                    background: white;
                }
                .ecc-search-input:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }
                .ecc-table-card {
                    background: white;
                    border-radius: 18px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
                    overflow: hidden;
                }
                .ecc-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .ecc-table th {
                    padding: 18px 24px;
                    text-align: left;
                    font-size: 12px;
                    font-weight: 800;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.07em;
                    background: #f8fafc;
                    border-bottom: 1.5px solid #e2e8f0;
                }
                .ecc-table td {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                }
                .ecc-table tr:last-child td {
                    border-bottom: none;
                }
                .ecc-table tr:hover td {
                    background: #fbfcfe;
                }
                .ecc-btn-view {
                    background: white;
                    color: #2563eb;
                    border: 1.5px solid #dbeafe;
                    padding: 8px 16px;
                    border-radius: 10px;
                    font-size: 13.5px;
                    font-weight: 700;
                    cursor: pointer;
                    font-family: inherit;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    transition: all 0.15s;
                }
                .ecc-btn-view:hover {
                    background: #eff6ff;
                    border-color: #bfdbfe;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
                }
                .ecc-empty {
                    padding: 70px 24px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 14px;
                }
            `}</style>

            <div className="ecc-container">
                {/* Header */}
                <div className="ecc-header">
                    <div>
                        <div className="ecc-badge">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                            Compliance & Risk Radar
                        </div>
                        <h1 className="ecc-title">Expiry Command Center</h1>
                        <p className="ecc-desc">Real-time compliance monitoring for expired driver credentials, fleet documentation, and client contracts.</p>
                    </div>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button 
                            onClick={fetchInitialData}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, background: 'white', border: '1.5px solid #cbd5e1',
                                padding: '10px 18px', borderRadius: 12, fontSize: 13.5, fontWeight: 700, color: '#334155', cursor: 'pointer',
                                transition: 'all 0.15s', fontFamily: 'inherit'
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#94a3b8'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                            Refresh Audit
                        </button>
                    </div>
                </div>

                {/* 4 KPI Summary Cards */}
                <div className="ecc-kpi-grid">
                    <div 
                        className={`ecc-kpi-card ${activeTab === 'All' ? 'active' : ''}`}
                        onClick={() => setActiveTab('All')}
                        title="Click to view all expired records"
                    >
                        <div className="ecc-kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div>
                            <div className="ecc-kpi-val" style={{ color: '#dc2626' }}>{totalExpiredCount}</div>
                            <div className="ecc-kpi-label">Total Expired Records</div>
                        </div>
                    </div>

                    <div 
                        className={`ecc-kpi-card ${activeTab === 'Drivers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Drivers')}
                        title="Click to view expired drivers"
                    >
                        <div className="ecc-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </div>
                        <div>
                            <div className="ecc-kpi-val">{expiredDriversList.length}</div>
                            <div className="ecc-kpi-label">Expired Drivers</div>
                        </div>
                    </div>

                    <div 
                        className={`ecc-kpi-card ${activeTab === 'Fleet & Vehicle' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Fleet & Vehicle')}
                        title="Click to view expired vehicles"
                    >
                        <div className="ecc-kpi-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13"></rect>
                                <polygon points="16 8 20 8 23 11 23 16 16 16 8"></polygon>
                                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                        </div>
                        <div>
                            <div className="ecc-kpi-val">{expiredVehiclesList.length}</div>
                            <div className="ecc-kpi-label">Expired Fleet & Vehicles</div>
                        </div>
                    </div>

                    <div 
                        className={`ecc-kpi-card ${activeTab === 'Clients & Contracts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('Clients & Contracts')}
                        title="Click to view expired client contracts"
                    >
                        <div className="ecc-kpi-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                        </div>
                        <div>
                            <div className="ecc-kpi-val">{expiredClientsList.reduce((sum, c) => sum + c.expiredItems.length, 0)}</div>
                            <div className="ecc-kpi-label">Expired Contracts & Docs</div>
                        </div>
                    </div>
                </div>

                {/* Tabs & Search Controls */}
                <div className="ecc-controls">
                    <div className="ecc-tabs-group">
                        <button 
                            className={`ecc-tab-btn ${activeTab === 'All' ? 'active' : ''}`}
                            onClick={() => setActiveTab('All')}
                        >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            All Expired
                            <span 
                                className="ecc-tab-badge"
                                style={{
                                    background: activeTab === 'All' ? (totalExpiredCount > 0 ? '#fee2e2' : '#f1f5f9') : '#e2e8f0',
                                    color: totalExpiredCount > 0 ? '#b91c1c' : '#64748b'
                                }}
                            >
                                {totalExpiredCount}
                            </span>
                        </button>

                        <button 
                            className={`ecc-tab-btn ${activeTab === 'Drivers' ? 'active' : ''}`}
                            onClick={() => setActiveTab('Drivers')}
                        >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            Drivers
                            <span 
                                className="ecc-tab-badge"
                                style={{
                                    background: activeTab === 'Drivers' ? (expiredDriversList.length > 0 ? '#fee2e2' : '#f1f5f9') : '#e2e8f0',
                                    color: expiredDriversList.length > 0 ? '#b91c1c' : '#64748b'
                                }}
                            >
                                {expiredDriversList.length}
                            </span>
                        </button>

                        <button 
                            className={`ecc-tab-btn ${activeTab === 'Fleet & Vehicle' ? 'active' : ''}`}
                            onClick={() => setActiveTab('Fleet & Vehicle')}
                        >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13"></rect>
                                <polygon points="16 8 20 8 23 11 23 16 16 16 8"></polygon>
                                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                            Fleet & Vehicles
                            <span 
                                className="ecc-tab-badge"
                                style={{
                                    background: activeTab === 'Fleet & Vehicle' ? (expiredVehiclesList.length > 0 ? '#fee2e2' : '#f1f5f9') : '#e2e8f0',
                                    color: expiredVehiclesList.length > 0 ? '#b91c1c' : '#64748b'
                                }}
                            >
                                {expiredVehiclesList.length}
                            </span>
                        </button>

                        <button 
                            className={`ecc-tab-btn ${activeTab === 'Clients & Contracts' ? 'active' : ''}`}
                            onClick={() => setActiveTab('Clients & Contracts')}
                        >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 21h18"></path>
                                <path d="M19 21v-4"></path>
                                <path d="M19 17a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v4"></path>
                                <path d="M9 3v12"></path>
                                <path d="M15 3v12"></path>
                            </svg>
                            Clients & Contracts
                            <span 
                                className="ecc-tab-badge"
                                style={{
                                    background: activeTab === 'Clients & Contracts' ? (expiredClientsList.length > 0 ? '#fee2e2' : '#f1f5f9') : '#e2e8f0',
                                    color: expiredClientsList.length > 0 ? '#b91c1c' : '#64748b'
                                }}
                            >
                                {expiredClientsList.reduce((s, c) => s + c.expiredItems.length, 0)}
                            </span>
                        </button>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 14, top: 13 }}>
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input 
                            type="text" 
                            className="ecc-search-input" 
                            placeholder="Search expired records..." 
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table Content */}
                <div className="ecc-table-card">
                    {loading ? (
                        <div className="ecc-empty">
                            <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                            <div style={{ color: '#64748b', fontSize: 15, fontWeight: 600 }}>Analyzing compliance records...</div>
                        </div>
                    ) : (
                        <>
                            {/* All View */}
                            {activeTab === 'All' && (
                                filteredAll.length === 0 ? (
                                    <div className="ecc-empty">
                                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                            </svg>
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>All Operations 100% Compliant</div>
                                        <div style={{ fontSize: 14.5, color: '#64748b', maxWidth: 460 }}>There are currently zero expired driver documents, vehicle permits, or client agreements across the entire platform.</div>
                                    </div>
                                ) : (
                                    <table className="ecc-table">
                                        <thead>
                                            <tr>
                                                <th>Entity Name</th>
                                                <th>Category</th>
                                                <th>Identifier / Details</th>
                                                <th>Contact / Ref</th>
                                                <th>Expired Item</th>
                                                <th>Expiry Date</th>
                                                <th style={{ textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAll.map(item => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            <div style={{
                                                                width: 42, height: 42, borderRadius: 12,
                                                                background: item.avatarBg,
                                                                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontWeight: 800, fontSize: 16
                                                            }}>
                                                                {item.avatarInitial}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15.5 }}>{item.name}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                            padding: '4px 10px',
                                                            borderRadius: 8,
                                                            background: item.categoryBg,
                                                            color: item.categoryColor,
                                                            border: `1px solid ${item.categoryBorder}`
                                                        }}>
                                                            {item.entityType}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#475569' }}>{item.code}</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: 14, color: '#334155', fontWeight: 600 }}>{item.contact}</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                                            </svg>
                                                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{item.expiredDetail}</span>
                                                        </div>
                                                    </td>
                                                    <td>{renderDateCell(item.expiryDate)}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button 
                                                            className="ecc-btn-view"
                                                            onClick={item.action}
                                                        >
                                                            {item.actionText}
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                                                <polyline points="12 5 19 12 12 19"></polyline>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}

                            {/* Drivers View */}
                            {activeTab === 'Drivers' && (
                                filteredDrivers.length === 0 ? (
                                    <div className="ecc-empty">
                                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                            </svg>
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>All Driver Documents Compliant</div>
                                        <div style={{ fontSize: 14.5, color: '#64748b', maxWidth: 460 }}>There are currently no expired Civil IDs, passports, or driver licenses registered for your operational drivers.</div>
                                    </div>
                                ) : (
                                    <table className="ecc-table">
                                        <thead>
                                            <tr>
                                                <th>Driver Details</th>
                                                <th>Contact Info</th>
                                                <th>Civil ID Expiry</th>
                                                <th>Passport Expiry</th>
                                                <th>License Expiry</th>
                                                <th style={{ textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDrivers.map(driver => (
                                                <tr key={driver._id}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            <div style={{
                                                                width: 42, height: 42, borderRadius: 12,
                                                                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                                                                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontWeight: 800, fontSize: 16
                                                            }}>
                                                                {(driver.name || 'D').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15.5 }}>{driver.name}</div>
                                                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{driver.driverCode || driver.driverId || 'ID Pending'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{driver.phone || '—'}</div>
                                                        <div style={{ fontSize: 13, color: '#64748b' }}>{driver.email || '—'}</div>
                                                    </td>
                                                    <td>{renderDateCell(driver.civilIdExpiry)}</td>
                                                    <td>{renderDateCell(driver.passportExpiry)}</td>
                                                    <td>{renderDateCell(driver.licenseExpiry)}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button 
                                                            className="ecc-btn-view"
                                                            onClick={() => handleNavigateTo('drivers', driver._id)}
                                                        >
                                                            View Profile
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                                                <polyline points="12 5 19 12 12 19"></polyline>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}

                            {/* Vehicles View */}
                            {activeTab === 'Fleet & Vehicle' && (
                                filteredVehicles.length === 0 ? (
                                    <div className="ecc-empty">
                                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                            </svg>
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>All Vehicles Fully Insured & Licensed</div>
                                        <div style={{ fontSize: 14.5, color: '#64748b', maxWidth: 460 }}>No vehicles in your fleet have expired insurance policies, registrations, or vehicle permits.</div>
                                    </div>
                                ) : (
                                    <table className="ecc-table">
                                        <thead>
                                            <tr>
                                                <th>Vehicle Identifier</th>
                                                <th>Make & Model</th>
                                                <th>Insurance Expiry</th>
                                                <th>Registration Expiry</th>
                                                <th>Permit / License</th>
                                                <th style={{ textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredVehicles.map(vehicle => (
                                                <tr key={vehicle._id}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            <div style={{
                                                                width: 42, height: 42, borderRadius: 12,
                                                                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                                                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontWeight: 800
                                                            }}>
                                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="1" y="3" width="15" height="13"></rect>
                                                                    <polygon points="16 8 20 8 23 11 23 16 16 8"></polygon>
                                                                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                                                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15.5 }}>{vehicle.plateNumber || 'No Plate'}</div>
                                                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{vehicle.vehicleCode || 'Code Pending'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: 14.5, fontWeight: 600, color: '#334155' }}>
                                                            {vehicle.make} {vehicle.model}
                                                        </div>
                                                        <div style={{ fontSize: 13, color: '#64748b' }}>{vehicle.year ? `Year ${vehicle.year}` : 'Fleet Asset'}</div>
                                                    </td>
                                                    <td>{renderDateCell(vehicle.insuranceExpiry)}</td>
                                                    <td>{renderDateCell(vehicle.registrationExpiry)}</td>
                                                    <td>{renderDateCell(vehicle.vehicleLicenseExpiry)}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button 
                                                            className="ecc-btn-view"
                                                            onClick={() => handleNavigateTo('all-vehicles', vehicle._id)}
                                                        >
                                                            View Asset
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                                                <polyline points="12 5 19 12 12 19"></polyline>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}

                            {/* Clients & Contracts View */}
                            {activeTab === 'Clients & Contracts' && (
                                filteredClients.length === 0 ? (
                                    <div className="ecc-empty">
                                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                            </svg>
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>All Client Contracts & Docs Active</div>
                                        <div style={{ fontSize: 14.5, color: '#64748b', maxWidth: 460 }}>No corporate client contracts, master agreements, or compliance filings have passed their expiration date.</div>
                                    </div>
                                ) : (
                                    <table className="ecc-table">
                                        <thead>
                                            <tr>
                                                <th>Client / Organization</th>
                                                <th>Primary Contact</th>
                                                <th>Expired Agreement / Doc</th>
                                                <th>Document Type</th>
                                                <th>Contract End Date</th>
                                                <th style={{ textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredClients.map(cData => (
                                                cData.expiredItems.map((item, idx) => (
                                                    <tr key={`${cData.client._id}-${idx}`}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                <div style={{
                                                                    width: 42, height: 42, borderRadius: 12,
                                                                    background: 'linear-gradient(135deg, #063A5C 0%, #1e40af 100%)',
                                                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontWeight: 800, fontSize: 16
                                                                }}>
                                                                    {(cData.client.companyName || 'C').charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15.5 }}>{cData.client.companyName}</div>
                                                                    <div style={{ fontSize: 13, color: '#64748b' }}>{cData.client.address || 'Address not set'}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ fontSize: 14.5, fontWeight: 600, color: '#334155' }}>
                                                                {cData.client.contactPerson || 'N/A'}
                                                            </div>
                                                            <div style={{ fontSize: 13, color: '#64748b' }}>{cData.client.email || cData.client.phone || '—'}</div>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                                </svg>
                                                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 14.5 }}>{item.title}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                                padding: '5px 12px',
                                                                borderRadius: 8,
                                                                background: item.type === 'Contract' ? '#eff6ff' : '#fef3c7',
                                                                color: item.type === 'Contract' ? '#1d4ed8' : '#b45309',
                                                                border: item.type === 'Contract' ? '1px solid #bfdbfe' : '1px solid #fde68a'
                                                            }}>
                                                                {item.type}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {renderDateCell(item.expiryDate)}
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <button 
                                                                className="ecc-btn-view"
                                                                onClick={() => handleNavigateTo('client-details', cData.client._id, 'contracts')}
                                                            >
                                                                View Client Details
                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                                    <polyline points="12 5 19 12 12 19"></polyline>
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
